import { z } from 'zod';

/**
 * ランチャー経由だけ許可（サーバーごと）。
 * ランチャーがゲームを起動するとき、Mojang の本人確認（join / hasJoined）で「この UUID はランチャーから来る」と
 * パネルに伝える。パネルは許可した UUID を期限つきで agent に配り、Paper 系は補助プラグインがログイン前に、
 * それ以外は agent が参加を見つけてキックする。
 * ランチャーは利用者の手元で動くので、作り込めば真似できる。荒らしの手間を増やすための仕組み。
 */
export const LauncherGateSettingsSchema = z
  .object({
    enabled: z.boolean(),
    /** 統合版（Geyser・Floodgate）はランチャーを使えないので、既定で通す */
    allowBedrock: z.boolean().default(true),
  })
  .strict();
export type LauncherGateSettings = z.infer<typeof LauncherGateSettingsSchema>;

/** 1 回の確認で入れる時間。ランチャーはゲームが動いている間、これより短い間隔で確認し直す */
export const LAUNCHER_PASS_TTL_MS = 10 * 60 * 1000;
export const LAUNCHER_PASS_RENEW_SECONDS = 300;

const Uuid = z
  .string()
  .regex(/^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i)
  .transform((value) => {
    const hex = value.replaceAll('-', '').toLowerCase();
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  });
export const LauncherPassEntrySchema = z
  .object({ uuid: Uuid, name: z.string().regex(/^\w{1,16}$/), until: z.number().int().nonnegative() })
  .strict();
export type LauncherPassEntry = z.infer<typeof LauncherPassEntrySchema>;
export const LauncherGateStateSchema = z
  .object({
    settings: LauncherGateSettingsSchema,
    allowed: z.array(LauncherPassEntrySchema).max(5000),
    message: z.string().max(300),
  })
  .strict();
export type LauncherGateState = z.infer<typeof LauncherGateStateSchema>;

/** ランチャーを使えない人（Android の Zalith Launcher など）を、すべての制限つきサーバーで通す */
export const LauncherGateExemptSchema = z
  .object({ uuid: Uuid, name: z.string().regex(/^\w{1,16}$/), addedAt: z.string() })
  .strict();
export type LauncherGateExempt = z.infer<typeof LauncherGateExemptSchema>;
export const LAUNCHER_GATE_EXEMPT_MAX = 500;
/** 例外の人を allowed に入れるときの期限（補助プラグイン・agent はそのまま期限として扱う） */
export const LAUNCHER_GATE_EXEMPT_UNTIL = Number.MAX_SAFE_INTEGER;

export const LauncherPassVerifySchema = z
  .object({ passId: z.string().min(16).max(100), username: z.string().regex(/^\w{1,16}$/) })
  .strict();

export const LAUNCHER_GATE_MESSAGE = 'このサーバーはランチャー（Shouchan Launcher）から起動したときだけ入れます。';

/** 補助プラグインが読む launcher-gate.txt。名前は書かない（UUID と期限だけ） */
export function launcherGateFile(state: LauncherGateState): string {
  const parsed = LauncherGateStateSchema.parse(state);
  return [
    '# mc-panel が書くファイル（手で編集しない）',
    `enabled=${parsed.settings.enabled}`,
    `bedrock=${parsed.settings.allowBedrock}`,
    `message=${btoa(String.fromCharCode(...new TextEncoder().encode(parsed.message)))}`,
    ...parsed.allowed.map((entry) => `allow ${entry.uuid} ${entry.until}`),
    '',
  ].join('\n');
}

/** Floodgate（統合版）の UUID は上位 64 bit が 0 */
export function isBedrockUuid(uuid: string) {
  return /^00000000-0000-0000-/.test(uuid);
}

/** `UUID of player Steve is 069a79f4-44e9-4726-a5be-fca90e38aaf5` */
export function parsePlayerUuid(line: string): { name: string; uuid: string } | undefined {
  const match = /^\[[^\]]*\](?: \[[^\]]*\])?: UUID of player ([.*]?\w{1,16}) is ([0-9a-f-]{32,36})$/i.exec(line.trim());
  if (!match) return undefined;
  const uuid = Uuid.safeParse(match[2]);
  return uuid.success ? { name: match[1]!, uuid: uuid.data } : undefined;
}

/** 入ってよいか（agent のキックで使う。補助プラグインも同じ判断をする） */
export function launcherGateAllows(state: LauncherGateState, uuid: string, now: number, op = false) {
  if (!state.settings.enabled || op) return true;
  if (state.settings.allowBedrock && isBedrockUuid(uuid)) return true;
  return state.allowed.some((entry) => entry.uuid === uuid && entry.until >= now);
}
